import { useState } from "react"
import Button from "@/v2-backoffice-client/components/button"
import TranslationAddRowDialog from "@/v2-backoffice-client/translation/addRow/TranslationAddRowDialog"
import type { TranslationBoard } from "@/v2-backoffice-client/translation/translation.types"

export type TranslationAddRowButtonProps = {
  board: TranslationBoard
  onSaved(): void
  // True until the catalog has loaded at least once — board is empty before
  // then (no languages, no type sets), so opening the dialog would show
  // nothing usable. Not the same as the query's raw `loading`, which also
  // flips true on every background refetch after a save; gating on that
  // would make this button flicker disabled during normal use.
  disabled?: boolean
}

// Page-header entry point for adding a single row (feature or data label)
// to the catalog. The dialog mounts only while open so its draft state
// resets per use. It lives above any specific catalog view, so it has no
// "active category" to preselect — Kind/typeSet/code-prefix always start
// blank.
//
// Text only: a feature created here gets the catalog's default type and
// availability, and its configuration stays empty. Both are edited
// afterwards on the row's own tabs.
function TranslationAddRowButton(props: TranslationAddRowButtonProps) {
  const { board, onSaved, disabled = false } = props
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        iconSrc="plus"
        data-feature-code="UI_BackofficeTranslation_Catalog_AddRow"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={
          disabled
            ? "Loading the catalog…"
            : "Add a single entry to the catalog: a new feature, or a new data label in an existing type set."
        }
      >
        Add entry…
      </Button>
      {open && (
        <TranslationAddRowDialog
          open
          onOpenChange={setOpen}
          board={board}
          category={null}
          onSaved={onSaved}
        />
      )}
    </>
  )
}

export default TranslationAddRowButton
